import React from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import CurrencyValue from "../CurrencyValue";

const HOneIngotDetailTable = ({
  ingots,
  manufacturingRates = [],
  custodyPercent = 0,
  spreadRate = 0,
  guaranteeMargin = 0,
}) => {
  const { t } = useTranslation();

  if (!ingots?.length) return null;

  const storageRate = Number(custodyPercent) / 100;
  const guaranteeRate = Number(guaranteeMargin) / 100;

  // The contract rows only carry ingot_gr / quantity, so per-ingot
  // manufacturing and storage are derived the same way they are at purchase
  // time (see hOneCalculation in helpers.js).
  const rows = ingots.map((item) => {
    const quantity = Number(item.quantity) || 0;
    const grams = Number(item.ingot_gr) * quantity;
    const manufacturingRate =
      manufacturingRates.find((m) => m.ingot === `${item.ingot_gr}gr`)?.cost ||
      0;
    const manufacturingCost = manufacturingRate * quantity;
    const goldCost = grams * Number(item.purchase_fixing || 0);
    const spreadCost = goldCost * Number(spreadRate);
    const storageCost =
      item.type === "home_delivery" ? 0 : goldCost * storageRate;

    // Guarantee margin applies to gold + spread + storage (not manufacturing),
    // same as hOneCalculation in helpers.js.
    const guaranteeCost = (goldCost + spreadCost + storageCost) * guaranteeRate;

    const totalCost =
      goldCost + spreadCost + manufacturingCost + storageCost + guaranteeCost;

    return {
      ...item,
      grams,
      goldCost,
      spreadCost,
      manufacturingCost,
      storageCost,
      guaranteeCost,
      totalCost,
    };
  });

  return (
    <Card className="mb-2" bg="dark1 text-light">
      <Card.Header className="text-uppercase">{t("ingot")}</Card.Header>
      <Card.Body>
        <DataTable
          value={rows}
          size={"small"}
          showGridlines
          stripedRows
          pt={{
            column: {
              bodyCell: {
                style: { fontSize: "0.6875rem" }
              },
              headerCell: {
                style: { fontSize: "0.875rem" }
              }
            }
          }}>
          <Column
            field="ingot_gr"
            header={t("ingot")}
            body={(rowData) => `${rowData.ingot_gr}gr`}
          />
          <Column field="quantity" header={t("quantity")} />
          <Column
            field="grams"
            header={t("gramsPurchased")}
            body={(rowData) => `${rowData.grams}gr`}
          />
          <Column
            field="goldCost"
            header={t("goldValue")}
            body={(rowData) => (
              <CurrencyValue
                amount={rowData.goldCost}
                currency={rowData.currency}
                rate={rowData.fx_rate}
              />
            )}
          />
          <Column
            field="spreadCost"
            header={t("spread")}
            body={(rowData) => (
              <CurrencyValue
                amount={rowData.spreadCost}
                currency={rowData.currency}
                rate={rowData.fx_rate}
              />
            )}
          />
          <Column
            field="manufacturingCost"
            header={t("manufacturing2")}
            body={(rowData) => (
              <CurrencyValue
                amount={rowData.manufacturingCost}
                currency={rowData.currency}
                rate={rowData.fx_rate}
              />
            )}
          />
          <Column
            field="storageCost"
            header={t("storageCost")}
            body={(rowData) => (
              <CurrencyValue
                amount={rowData.storageCost}
                currency={rowData.currency}
                rate={rowData.fx_rate}
              />
            )}
          />
          <Column
            field="guaranteeCost"
            header={`${t("guaranteeMargin2")} ${guaranteeMargin}%`}
            body={(rowData) => (
              <CurrencyValue
                amount={rowData.guaranteeCost}
                currency={rowData.currency}
                rate={rowData.fx_rate}
              />
            )}
          />
          <Column
            field="totalCost"
            header={t("total")}
            body={(rowData) => (
              <CurrencyValue
                amount={rowData.totalCost}
                currency={rowData.currency}
                rate={rowData.fx_rate}
              />
            )}
          />
          <Column />
        </DataTable>
      </Card.Body>
    </Card>
  );
};

export default HOneIngotDetailTable;
